import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "../api";
import { readableOn } from "../lib/colors";
import { CalendarDays, MapPin, Ticket } from "lucide-react";

const dayLabel = (iso) => {
  if (!iso) return "Date to be announced";
  try { return new Date(iso).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" }); }
  catch { return "Date to be announced"; }
};
const timeLabel = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};

export default function EmbedEvents() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [cat, setCat] = useState("all");
  const [narrow, setNarrow] = useState(typeof window !== "undefined" && window.innerWidth < 480);
  const rootRef = useRef(null);

  useEffect(() => {
    const onR = () => setNarrow(window.innerWidth < 480);
    onR();
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, []);

  useEffect(() => {
    const bodyBg = document.body.style.background;
    const htmlBg = document.documentElement.style.background;
    document.body.style.background = "transparent";
    document.documentElement.style.background = "transparent";
    return () => { document.body.style.background = bodyBg; document.documentElement.style.background = htmlBg; };
  }, []);

  useEffect(() => {
    api.get(`/store/${slug}`)
      .then(({ data }) => setData(data))
      .catch(() => setError("Store not found"));
  }, [slug]);

  useEffect(() => {
    const post = () => {
      const h = rootRef.current ? rootRef.current.offsetHeight : document.body.scrollHeight;
      if (window.parent !== window) {
        window.parent.postMessage({ type: "ticketspro-embed-height", path: window.location.pathname, height: h }, "*");
      }
    };
    post();
    const t = setTimeout(post, 300);
    let ro;
    if (rootRef.current && "ResizeObserver" in window) { ro = new ResizeObserver(post); ro.observe(rootRef.current); }
    window.addEventListener("load", post);
    return () => { clearTimeout(t); if (ro) ro.disconnect(); window.removeEventListener("load", post); };
  }, [data, error, cat, narrow]);

  if (error) return <div ref={rootRef} style={{ fontFamily: "system-ui, sans-serif", padding: 16, color: "#5a6b80" }}>{error}</div>;
  if (!data) return <div ref={rootRef} style={{ padding: 16 }} />;

  const b = data.business;
  const layout = params.get("layout") === "compact" ? "compact" : "detailed";
  const sharp = params.get("corners") === "sharp";
  const r = (v) => (sharp ? 0 : v);
  const pill = sharp ? 4 : 999;

  // Custom background/text colours (embed-only). Background luminance decides light/dark styling.
  const bgParam = params.get("bg") ? `#${params.get("bg").replace(/^#/, "")}` : "";
  const txtParam = params.get("txt") ? `#${params.get("txt").replace(/^#/, "")}` : "";
  const hexLum = (hex) => {
    const h = (hex || "").replace("#", "");
    if (h.length < 6) return 1;
    const rr = parseInt(h.slice(0, 2), 16), gg = parseInt(h.slice(2, 4), 16), bb = parseInt(h.slice(4, 6), 16);
    return (0.299 * rr + 0.587 * gg + 0.114 * bb) / 255;
  };
  const dark = bgParam ? hexLum(bgParam) < 0.5 : params.get("theme") === "dark";

  const brand = b.brand_color || "#16243b";
  const btnColor = b.button_color || "#2ec9c9";
  const accent = b.accent_color || btnColor;
  const btnText = b.embed_button_text_color || readableOn(btnColor);
  const cardBg = bgParam || (dark ? "#111827" : "#ffffff");
  const rowBg = cardBg;
  const border = dark ? "rgba(255,255,255,0.12)" : "#eef1f5";
  const baseText = txtParam || b.embed_text_color || b.text_color || (dark ? "#f1f5f9" : "#16243b");
  const muted = dark ? "rgba(255,255,255,0.65)" : "#5a6b80";
  const subMuted = dark ? "rgba(255,255,255,0.45)" : "#8a97a8";
  const shadow = dark ? "0 6px 22px rgba(0,0,0,0.45)" : "0 6px 22px rgba(20,30,55,0.12)";
  const origin = window.location.origin;

  const all = (data.products || []).filter((p) => !p.archived);
  const categories = [...new Set(all.map((p) => p.category).filter(Boolean))];
  const events = cat === "all" ? all : all.filter((p) => p.category === cat);

  const groups = [];
  for (const p of events) {
    const key = (p.event_date || "tba").slice(0, 10);
    let g = groups.find((x) => x.key === key);
    if (!g) { g = { key, label: p.event_date ? dayLabel(p.event_date) : "Date to be announced", events: [] }; groups.push(g); }
    g.events.push(p);
  }

  const ctaFor = (p) => {
    if (p.cancelled) return { text: "Cancelled", disabled: true };
    if (p.tickets_disabled) return { text: "More info", disabled: false };
    if (p.sold_out) return { text: "Sold out", disabled: true };
    const prices = p.ticket_tiers && p.ticket_tiers.length ? p.ticket_tiers.map((t) => t.price) : [p.price];
    return { text: Math.min(...prices) <= 0 ? "Get free ticket" : "Get tickets", disabled: false };
  };
  const priceLabel = (p) => {
    if (p.tickets_disabled) return "Free event";
    const prices = p.ticket_tiers && p.ticket_tiers.length ? p.ticket_tiers.map((t) => t.price) : [p.price];
    const from = Math.min(...prices);
    return from <= 0 ? "Free" : `${new Set(prices).size > 1 ? "from " : ""}£${from.toFixed(2)}`;
  };

  const Meta = ({ p }) => (
    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginTop: layout === "compact" ? 4 : 8, fontSize: 12.5, color: muted }}>
      {p.event_date && <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontWeight: 600 }}><CalendarDays size={14} color={accent} /> {timeLabel(p.event_date)}</span>}
      {p.venue_name && <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><MapPin size={14} color={accent} /> {p.venue_name}</span>}
      {p.category && <span style={{ background: dark ? `${accent}26` : `${brand}14`, color: dark ? accent : brand, fontWeight: 600, borderRadius: pill, padding: "2px 9px", fontSize: 11.5 }}>{p.category}</span>}
    </div>
  );

  const Thumb = ({ p, size = 72 }) => {
    if (p.image_url) return <img src={p.image_url} alt="" style={{ width: size, height: size, objectFit: "cover", borderRadius: r(10), flexShrink: 0 }} />;
    return (
      <div style={{ width: size, height: size, borderRadius: r(10), flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: dark ? `${accent}22` : `${brand}12` }}>
        <Ticket size={Math.round(size * 0.36)} color={accent} />
      </div>
    );
  };

  return (
    <div ref={rootRef} style={{ fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif", padding: 4, boxSizing: "border-box" }} data-testid="embed-events-root" data-layout={layout} data-theme={dark ? "dark" : "light"}>
      <div style={{ background: cardBg, borderRadius: r(16), boxShadow: shadow, overflow: "hidden", border: dark ? `1px solid ${border}` : "none" }}>
        <div style={{ padding: "18px 20px", borderBottom: `1px solid ${border}`, display: "flex", alignItems: "center", gap: 12 }}>
          {b.logo_url && <img src={b.logo_url} alt="" style={{ height: 40, width: 40, objectFit: "contain", borderRadius: r(8) }} />}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 17, fontWeight: 800, color: baseText, lineHeight: 1.2 }} data-testid="embed-events-title">{b.name}</div>
            <div style={{ fontSize: 12.5, color: subMuted, marginTop: 2 }}>{all.length} event{all.length !== 1 ? "s" : ""}</div>
          </div>
        </div>

        {categories.length > 1 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, padding: "12px 20px 4px" }} data-testid="embed-events-tabs">
            {["all", ...categories].map((c) => {
              const active = cat === c;
              return (
                <button key={c} onClick={() => setCat(c)} data-testid={`embed-tab-${c}`}
                  style={{ border: "none", cursor: "pointer", fontSize: 12.5, fontWeight: 600, borderRadius: pill, padding: "6px 14px",
                    background: active ? brand : (dark ? "rgba(255,255,255,0.1)" : "#f1f4f8"), color: active ? "#fff" : muted }}>{c === "all" ? "All" : c}</button>
              );
            })}
          </div>
        )}

        <div style={{ padding: "8px 20px 20px" }}>
          {groups.length === 0 ? (
            <p style={{ color: subMuted, fontSize: 14, padding: "20px 0" }} data-testid="embed-events-empty">No events on sale right now. Please check back soon.</p>
          ) : groups.map((g) => (
            <div key={g.key} style={{ marginTop: 16 }}>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: subMuted, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 8 }}>{g.label}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {g.events.map((p) => {
                  const cta = ctaFor(p);
                  const href = `${origin}/store/${b.slug}/events/${p.slug}`;
                  const titleEl = <div style={{ fontSize: layout === "compact" ? 14.5 : 15.5, fontWeight: 700, color: baseText, lineHeight: 1.25 }}>{p.title}</div>;
                  const descEl = layout === "detailed" && p.description
                    ? <div style={{ fontSize: 13, color: muted, marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{p.description}</div>
                    : null;
                  const priceEl = <div style={{ fontSize: layout === "compact" ? 14.5 : 16, fontWeight: 800, color: b.accent_color || baseText, marginBottom: 8, whiteSpace: "nowrap" }} data-testid={`embed-price-${p.slug}`}>{priceLabel(p)}</div>;
                  const btnEl = (
                    <a href={cta.disabled ? undefined : href} target="_blank" rel="noopener noreferrer" data-testid={`embed-cta-${p.slug}`}
                      style={{ textDecoration: "none", display: "inline-block", fontSize: 13, fontWeight: 600, color: cta.disabled ? "#fff" : btnText, borderRadius: pill,
                        padding: "8px 16px", whiteSpace: "nowrap", background: cta.disabled ? "#9aa7b8" : btnColor, pointerEvents: cta.disabled ? "none" : "auto" }}>{cta.text}</a>
                  );
                  return (
                    <div key={p.id} data-testid={`embed-event-${p.slug}`}
                      style={{ border: `1px solid ${border}`, background: rowBg, borderRadius: r(12), padding: layout === "compact" ? "10px 14px" : 14,
                        display: "flex", flexDirection: narrow ? "column" : "row", alignItems: narrow ? "stretch" : "center", gap: narrow ? 8 : 14 }}>
                      {narrow ? (
                        <>
                          {titleEl}
                          <Meta p={p} />
                          <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 2 }}>
                            {layout === "detailed" && <Thumb p={p} size={60} />}
                            <div style={{ marginLeft: "auto", textAlign: "right" }}>
                              <div style={{ fontSize: layout === "compact" ? 15 : 17, fontWeight: 800, color: baseText, marginBottom: 6, whiteSpace: "nowrap" }}>{priceLabel(p)}</div>
                              {btnEl}
                            </div>
                          </div>
                          {descEl}
                        </>
                      ) : (
                        <>
                          {layout === "detailed" && <Thumb p={p} />}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            {titleEl}
                            {descEl}
                            <Meta p={p} />
                          </div>
                          <div style={{ textAlign: "right", flexShrink: 0 }}>
                            {priceEl}
                            {btnEl}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div style={{ padding: "10px 20px", borderTop: `1px solid ${border}`, textAlign: "right" }}>
          <span style={{ fontSize: 11, color: subMuted }}>Powered by TicketsPro.app</span>
        </div>
      </div>
    </div>
  );
}
